import dayjs, { type Dayjs } from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getReservationReports } from '../../../api/endpoints/reports';
import type { ReservationReportRow } from '../../../api/types/reports';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ReservationIdentifierLink } from '../../../components/ReservationIdentifierLink';

const filterSchema = z
  .object({
    startDate: z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
      message: 'Başlangıç tarihi seçmelisiniz.',
    }),
    endDate: z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
      message: 'Bitiş tarihi seçmelisiniz.',
    }),
  })
  .refine((data) => !data.endDate.isBefore(data.startDate, 'day'), {
    message: 'Bitiş tarihi başlangıç tarihinden önce olamaz.',
    path: ['endDate'],
  });

type FilterValues = z.infer<typeof filterSchema>;

/** Tries several plausible field names in priority order — see api/types/reports.ts's
 *  header for why: the row shape is confirmed for Air/Transfer but Hotel/Bus/RentCar
 *  rows were never populated in this dev DB, so this stays defensive per-field rather
 *  than assuming every provider's row looks exactly like the confirmed ones. */
function pickField(row: ReservationReportRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return String(value);
  }
  return '—';
}

/**
 * "Rezervasyon Listesi" report for RentCar — thin filter form over the shared
 * `Report/Reservations` endpoint (see api/endpoints/reports.ts / api/types/reports.ts),
 * fixed to `providerType: 'RentCar'`. Rendered as a plain MUI Table (no
 * @mui/x-data-grid — incompatible with this project's pinned TypeScript 4.9.5). No
 * populated RentCar rows were found in the local dev DB while confirming the endpoint
 * (only Air and Transfer had real data there). No inline "Detay" — the PNR (or ticket
 * number) is a link into `ReservationDetailPage`, matching every other reservation list
 * in the app. `status` is no longer a user-facing filter — this list only ever shows
 * actually booked reservations, so the request is always sent with the fixed
 * `status: 'Booked'`.
 */
export default function RentCarReservationsPage() {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      startDate: dayjs(),
      endDate: dayjs(),
    },
  });

  const searchMutation = useMutation({
    mutationFn: (values: FilterValues) =>
      getReservationReports({
        insertStartDate: values.startDate.format('YYYY-MM-DD'),
        insertEndDate: values.endDate.format('YYYY-MM-DD'),
        status: 'Booked',
        providerType: 'RentCar',
      }),
  });

  const rows = searchMutation.data?.reservationReports ?? [];
  const hasSearched = searchMutation.isSuccess || searchMutation.isError;

  const onSubmit = handleSubmit((values) => searchMutation.mutate(values));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Araç Kiralama Rezervasyon Listesi
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={onSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Başlangıç tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.startDate,
                      helperText: errors.startDate?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Bitiş tarihi"
                  value={field.value}
                  onChange={field.onChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.endDate,
                      helperText: errors.endDate?.message,
                    },
                  }}
                />
              )}
            />
            <Button type="submit" variant="contained" disabled={searchMutation.isPending} sx={{ mt: { xs: 0, sm: 0.5 } }}>
              {searchMutation.isPending ? 'Aranıyor…' : 'Ara'}
            </Button>
          </Stack>
        </Box>
        <ApiErrorAlert error={searchMutation.error} sx={{ mt: 2 }} />
      </Paper>

      {searchMutation.isPending && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {hasSearched && !searchMutation.isError && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PNR</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Sağlayıcı</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Güzergah</TableCell>
                <TableCell>Toplam Ücret</TableCell>
                <TableCell>Rezervasyon Tarihi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Bu kriterlere uygun rezervasyon bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={pickField(row, ['shoppingCartId']) + index} hover>
                  <TableCell>
                    <ReservationIdentifierLink row={row} detailBasePath="/rent-car/reservations" />
                  </TableCell>
                  <TableCell>{pickField(row, ['statu', 'status'])}</TableCell>
                  <TableCell>{pickField(row, ['provider'])}</TableCell>
                  <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>{[row.origin, row.destination].filter(Boolean).join(' → ') || '—'}</TableCell>
                  <TableCell>
                    {pickField(row, ['totalFare'])} {pickField(row, ['currency']) !== '—' ? pickField(row, ['currency']) : ''}
                  </TableCell>
                  <TableCell>{pickField(row, ['bookedAt', 'insertedAt', 'createdAt'])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
